import {
    Divider,
    Paper,
    Typography,
    Grid,
    styled,
    IconButton,
    Stack,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type { IApplicationFilterItem } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import {
    ApplicationFiltersStore,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { useStore } from "utils/useStore";
import ApplicationFilterItem from "../FiltersAndChips/BaseFilterComponents/ApplicationFilterItem";
import SaveFiltersButton from "./SavedFilters/SaveFiltersButton";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { SavedFilterSettings } from "./SavedFilters/SavedFilterSettings";
import { AddOutlined, Close } from "@mui/icons-material";
import { ApplicationFilterDomain } from "services/ApplicationFiltersService";
import ConfirmUpdateFilterDialog from "components/UI/ConfirmUpdateFilterDialog";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import FilterBuilderAddFilterSelect from "../FiltersAndChips/BaseFilterComponents/FilterBuilderAddFilterSelect";

const SectionContainer = styled(Grid)(({ theme }) => ({
    paddingInline: theme.spacing(3),
    paddingBlock: theme.spacing(2.5),
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.grey[700],
    fontSize: "20px",
}));

const SubtitleTypography = styled(Typography)(({ theme }) => ({
    color: "#334155",
    fontSize: "14px",
}));

const FilterItemPaper = styled(Paper)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    boxShadow: "0px 1px 2px 0px #00000012",
    transition: "opacity 0.3s ease",
    '& [id^="remove-filter"]': {
        opacity: "0",
    },
    '&:hover [id^="remove-filter"]': {
        opacity: "1",
    },
}));

const FiltersBarContainer = styled(Grid)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[300]}`,
}));

interface ManageApplicationFiltersProps {
    title?: string;
    subtitle?: string;
    onCancel?(): void;
    onApply?(instanceStore: ApplicationFiltersInstanceStore): void;
    domainName?: string;
    setIsManageFiltersOpen: (open: boolean) => void;
}

const ManageApplicationFilters = observer(function (
    props: ManageApplicationFiltersProps,
) {
    const messageStore = useStore(MessageStore);
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const signalsPageStore = useStore(SignalsPageStore);

    const [isSelectFilterVisible, setIsSelectFilterVisible] = useState(false);

    const [showConfirmUpdateFilter, setShowConfirmUpdateFilter] =
        useState(false);

    const [updatedFilter, setUpdatedFilter] = useState<
        SavedFilter | undefined
    >();
    const selectedSavedFilter = applicationFiltersStore.selectedSavedFilter;

    const instanceStore = useMemo(() => {
        const instanceStore = applicationFiltersStore.toInstance();
        applicationFilterItemsStore.updateSelectedFilterItems(
            applicationFiltersStore.selectedSavedFilter
                ? instanceStore
                : applicationFiltersStore,
        );

        instanceStore.saveCurrentFilterState("Update");
        return instanceStore;
    }, [applicationFiltersStore, applicationFilterItemsStore]);

    // set current filter state to check for changes
    useEffect(() => {
        instanceStore.saveCurrentFilterState("LoadSaved");
        applicationFiltersStore.quickFiltersStore.saveCurrentFilterState(
            "LoadSaved",
        );
    }, [
        selectedSavedFilter?.id,
        instanceStore,
        applicationFiltersStore.quickFiltersStore,
    ]);

    useEffect(() => {
        setUpdatedFilter(undefined);
    }, [selectedSavedFilter?.id]);

    const filtersChanged =
        instanceStore.hasFilterStateChangedSince("LoadSaved") ||
        applicationFiltersStore.quickFiltersStore.hasFilterStateChangedSince(
            "LoadSaved",
        );

    const metadataChanged =
        !!updatedFilter &&
        !!selectedSavedFilter &&
        (updatedFilter.name !== selectedSavedFilter.name ||
            updatedFilter.domain !== selectedSavedFilter.domain);

    const filterLabel = useMemo(() => {
        const appliedFilterCount = instanceStore.appliedFilterCount ?? 0;

        if (appliedFilterCount === 1) return "1 Filter Applied";
        return `${appliedFilterCount} Filters Applied`;
    }, [instanceStore.appliedFilterCount]);

    const isFilterPinnedToAnyDashboards = signalsPageStore.userDashboards.some(
        (d) =>
            d.pinnedApplicationFilterId ===
            applicationFiltersStore.selectedSavedFilter?.id,
    );
    const isFilterSharedAndPinnedToAnyDashboards =
        applicationFiltersStore.selectedSavedFilter?.domain ===
            ApplicationFilterDomain.Organization &&
        isFilterPinnedToAnyDashboards;

    const shouldResetBuilder = signalsPageStore.shouldResetBuilder;
    const selectedSavedFilterId =
        applicationFiltersStore.selectedSavedFilter?.id;

    const handleClearFilters = useCallback(() => {
        applicationFilterItemsStore.setSelectedFilterItems([]);
        applicationFiltersStore.setSelectedSavedFilters();
        instanceStore.resetFilters();
    }, [applicationFilterItemsStore, applicationFiltersStore, instanceStore]);

    useEffect(() => {
        if (!shouldResetBuilder) return;

        // If a saved filter is currently selected, a stale reset signal should
        // not wipe that selection when reopening the builder.
        if (selectedSavedFilterId) {
            signalsPageStore.setShouldResetBuilder(false);
            return;
        }

        handleClearFilters();
        signalsPageStore.setShouldResetBuilder(false);
    }, [
        shouldResetBuilder,
        selectedSavedFilterId,
        signalsPageStore,
        handleClearFilters,
    ]);

    const filtersAppliedHeaderBar = (
        <FiltersBarContainer
            container
            item
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="nowrap"
            width="100%"
        >
            <Grid item>
                <Typography>{filterLabel}</Typography>
            </Grid>
            <Grid
                container
                item
                columnSpacing={1}
                alignItems="center"
                width="fit-content"
            >
                <Grid item>
                    <AcxButton
                        id="signals-filters-clear-builder"
                        color="primary"
                        variant="text"
                        onClick={handleClearFilters}
                        sx={(theme) => ({
                            color: theme.palette.primary.main,
                            ":hover": {
                                backgroundColor: theme.palette.white.main,
                            },
                        })}
                    >
                        Clear
                    </AcxButton>
                </Grid>
            </Grid>
        </FiltersBarContainer>
    );

    const renderFilterItem = (item: IApplicationFilterItem, i: number) => {
        let store = instanceStore;
        if (
            !applicationFiltersStore.selectedSavedFilter &&
            (item.filterKey === "dateRange" ||
                item.filterKey === "dateReferenceOption" ||
                item.filterKey === "hierarchyIds")
        ) {
            store = applicationFiltersStore.quickFiltersStore;
        }

        const isNotFirstItem = i !== 0;

        const andDivider = (
            <Grid
                container
                item
                direction="row"
                spacing={1}
                alignItems="center"
            >
                <Grid item>
                    <Typography
                        variant="body1"
                        color="textSecondary"
                        sx={(theme) => ({
                            fontWeight: "bold",
                            color: theme.palette.gray[400],
                            userSelect: "none",
                        })}
                    >
                        AND
                    </Typography>
                </Grid>
                <Grid item flexGrow={1}>
                    <Divider />
                </Grid>
            </Grid>
        );

        return (
            <React.Fragment key={item.filterKey}>
                {isNotFirstItem && andDivider}
                <Grid item flexShrink={1}>
                    <FilterItemPaper>
                        <ApplicationFilterItem
                            {...item}
                            store={store}
                            instanceStore={instanceStore}
                        />
                    </FilterItemPaper>
                </Grid>
            </React.Fragment>
        );
    };

    const currentlySelectedFilterItems =
        applicationFilterItemsStore.selectedFilterItems.map(renderFilterItem);

    const selectNewFilterDropdown = (
        <Grid item>
            <FilterItemPaper>
                <FilterBuilderAddFilterSelect<IApplicationFilterItem>
                    id="add-filter-select"
                    options={applicationFilterItemsStore.availableFilterGroups.map(
                        (group) => ({
                            label: group.title,
                            options: group.items,
                        }),
                    )}
                    onChange={(option?: IApplicationFilterItem) => {
                        if (!option) return;
                        applicationFilterItemsStore.addSelectedFilterItem(
                            option,
                        );
                        setIsSelectFilterVisible(false);
                    }}
                />
            </FilterItemPaper>
        </Grid>
    );

    const addFilterButton = (
        <Grid item sx={(theme) => ({ paddingBottom: theme.spacing(2.5) })}>
            <AcxButton
                color="primary"
                onClick={() => setIsSelectFilterVisible(true)}
                sx={(theme) => ({
                    color: theme.palette.blue.main,
                    backgroundColor: theme.palette.white.main,
                    paddingInline: theme.spacing(2),
                    paddingBlock: theme.spacing(1),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.gray[200]}`,
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    ":hover": {
                        backgroundColor: theme.palette.white.main,
                        opacity: "80%",
                    },
                })}
                startIcon={<AddOutlined sx={{ fontSize: "20px" }} />}
            >
                Filter
            </AcxButton>
        </Grid>
    );

    const savedFilterSettings =
        !!applicationFiltersStore.selectedSavedFilter && (
            <Grid
                container
                item
                sx={{
                    width: "fit-content",
                    position: "sticky",
                    top: 0,
                }}
            >
                <FilterItemPaper sx={{ height: "fit-content" }}>
                    <SavedFilterSettings
                        defaultSavedFilter={
                            applicationFiltersStore.selectedSavedFilter
                        }
                        onSavedFilterChange={(filter) =>
                            setUpdatedFilter(filter)
                        }
                        disabled={
                            !applicationFiltersStore.isSelectedSavedFilterEditable
                        }
                        isPrivatable={!isFilterSharedAndPinnedToAnyDashboards}
                        isDeletable={!isFilterPinnedToAnyDashboards}
                        apply={() => {
                            if (!signalsPageStore.currentDashboard) return;
                            signalsPageStore.currentDashboard.applyFilters();
                        }}
                        setIsManageFiltersOpen={
                            props.setIsManageFiltersOpen
                        }
                    />
                </FilterItemPaper>
            </Grid>
        );

    const applyAndClearSavedFilterButton = (
        <AcxButton
            color="primary"
            // A filter a component has flagged cannot be applied. The message sits on the control
            // that caused it, so the button going quiet points back at something visible.
            disabled={
                instanceStore.hasError ||
                instanceStore.wordsAndPhrasesStore.hasError
            }
            onClick={async () => {
                const valid = await instanceStore.validateWordsAndPhrases();
                if (!valid)
                    return messageStore.logError(
                        "Invalid Words and Phrases Syntax, please correct the following before continuing: \n" +
                            instanceStore.wordsAndPhrasesStore
                                .wordsPhrasesFtsSyntaxError,
                    );

                props.onApply?.(instanceStore);

                if (savedFilterIsSelected && filtersChanged) {
                    applicationFiltersStore.setSelectedSavedFilters();
                }
            }}
        >
            Apply
        </AcxButton>
    );

    const handleClickUpdateFilter = () => {
        const storageKey = `hasConfirmChangePinnedFilterModalShown-${applicationFiltersStore.selectedSavedFilter?.id}`;

        const filterHasAnySubscriptions =
            applicationFiltersStore.selectedSavedFilter
                ?.hasDashboardSubscription ||
            !!applicationFiltersStore.selectedSavedFilter
                ?.applicationFilterSubscriptionId ||
            applicationFiltersStore.selectedSavedFilter
                ?.hasOtherApplicationFilterSubscription;

        const showConfirmPinChange =
            applicationFiltersStore.authStore.isUserUltra() &&
            filterHasAnySubscriptions &&
            !sessionStorage.getItem(storageKey);
        if (showConfirmPinChange) {
            setShowConfirmUpdateFilter(true);
            sessionStorage.setItem(storageKey, "1");
        } else {
            handleSave();
        }
    };

    const handleSave = async () => {
        if (!applicationFiltersStore.isSelectedSavedFilterEditable) return;

        instanceStore.saveCurrentFilterState("LoadSaved");
        applicationFiltersStore.quickFiltersStore.saveCurrentFilterState(
            "LoadSaved",
        );

        const success =
            await applicationFiltersStore.updateSelectedSavedFilterWith(
                instanceStore,
                applicationFilterValuesStore,
                updatedFilter?.name,
                updatedFilter?.domain,
            );

        if (!success)
            return messageStore.logError(
                "Failed to update filter. Please try again.",
            );

        messageStore.logInfo(
            `'${
                applicationFiltersStore.selectedSavedFilter!.name
            }' successfully updated.`,
        );
    };

    const saveButton = (
        <AcxButton
            id="signals-filters-update-save-and-apply"
            color="white"
            disabled={
                instanceStore.hasError ||
                instanceStore.wordsAndPhrasesStore.hasError ||
                (isFilterSharedAndPinnedToAnyDashboards &&
                    updatedFilter?.domain === ApplicationFilterDomain.User) ||
                (!filtersChanged && !metadataChanged)
            }
            loading={applicationFiltersStore.getTaskLoading(
                ApplicationFiltersStore.Tasks.UPDATE_FILTER,
            )}
            onClick={handleClickUpdateFilter}
        >
            Save
        </AcxButton>
    );

    // this is saveAsNew when there is a saved filter and just "save" when no saved filter
    const saveNewOrUpdateFilterButton =
        !!applicationFiltersStore.selectedSavedFilter ? (
            <SaveFiltersButton
                store={instanceStore}
                color="white"
                variant="dialog"
                buttonVariant={"text"}
                showVisibilityToggle
                defaultFilter={updatedFilter}
            >
                Save as New
            </SaveFiltersButton>
        ) : (
            <SaveFiltersButton
                id="signals-filters-save"
                store={instanceStore}
                color="white"
                variant="dialog"
                showVisibilityToggle
            >
                Save
            </SaveFiltersButton>
        );

    const savedFilterIsSelected = !!applicationFiltersStore.selectedSavedFilter;
    const savedFilterIsEditable =
        !!applicationFiltersStore.isSelectedSavedFilterEditable;

    return (
        <Grid
            container
            direction="column"
            sx={{ height: "100%" }}
            flexWrap="nowrap"
        >
            <SectionContainer
                container
                item
                direction="column"
                flexWrap="nowrap"
            >
                <Grid
                    container
                    item
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item>
                        <TitleTypography>
                            {props.title ?? "Filters"}
                        </TitleTypography>
                    </Grid>
                    <Grid item>
                        <IconButton size="small" onClick={props.onCancel}>
                            <Close />
                        </IconButton>
                    </Grid>
                </Grid>
                <Grid item>
                    <SubtitleTypography>
                        {props.subtitle ??
                            `Define the criteria that will determine which
                        conversations are included or excluded in Signals
                        reporting.`}
                    </SubtitleTypography>
                </Grid>
            </SectionContainer>
            <SectionContainer
                container
                item
                direction="row"
                columnGap={3}
                flexGrow={1}
                flexWrap="nowrap"
                sx={(theme) => ({
                    overflowY: "auto",
                    backgroundColor: theme.palette.grey[50],
                })}
            >
                {savedFilterSettings}
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={2}
                >
                    {filtersAppliedHeaderBar}

                    {currentlySelectedFilterItems}

                    {applicationFilterItemsStore.selectedFilterItems.length >
                        0 && (
                        <Grid item>
                            <Divider />
                        </Grid>
                    )}

                    {isSelectFilterVisible && selectNewFilterDropdown}

                    {addFilterButton}
                </Grid>
            </SectionContainer>
            <SectionContainer
                container
                item
                sx={{
                    justifyContent: "flex-end",
                }}
                columnSpacing={1}
            >
                {/* "Secondary" or "Tertiary" button */}
                <Grid item>{saveNewOrUpdateFilterButton}</Grid>
                {/* "Primary" button(s) */}
                <Grid item>
                    <Stack direction={"row"} gap={1}>
                        {savedFilterIsEditable && saveButton}

                        {applyAndClearSavedFilterButton}
                    </Stack>
                </Grid>
            </SectionContainer>
            <ConfirmUpdateFilterDialog
                showConfirmUpdateFilter={showConfirmUpdateFilter}
                setShowConfirmUpdateFilter={setShowConfirmUpdateFilter}
                handleConfirm={handleSave}
            />
        </Grid>
    );
});

export default ManageApplicationFilters;
